import { useState, useEffect } from 'react';
import { NostrWebLNProvider } from '@getalby/sdk/webln';

export interface NWCConnection {
  connectionString: string;
  alias?: string;
}

/**
 * Hook to manage and execute Nostr Wallet Connect (NWC / NIP-47) actions via Alby SDK
 */
export function useNWC() {
  const [connections, setConnections] = useState<NWCConnection[]>(() => {
    try {
      const stored = localStorage.getItem('nwc-connections');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const [activeConnection, setActiveConnectionState] = useState<string | null>(() => {
    return localStorage.getItem('nwc-active-connection');
  });

  useEffect(() => {
    localStorage.setItem('nwc-connections', JSON.stringify(connections));
  }, [connections]);

  useEffect(() => {
    if (activeConnection) {
      localStorage.setItem('nwc-active-connection', activeConnection);
    } else {
      localStorage.removeItem('nwc-active-connection');
    }
  }, [activeConnection]);

  const addConnection = async (uri: string, alias?: string) => {
    const verified = uri.startsWith('nostr+walletconnect://') || uri.startsWith('nostrwalletconnect://');
    if (!verified) return false;

    // Direct test connection via Alby SDK NWC provider
    try {
      const nwcProvider = new NostrWebLNProvider({
        nostrWalletConnectUrl: uri
      });
      // Just try to enable/ping it to confirm it works
      await nwcProvider.enable();
      nwcProvider.close();

      const exists = connections.some(c => c.connectionString === uri);
      if (!exists) {
        setConnections(prev => [...prev, { connectionString: uri, alias: alias || 'Lightning Wallet' }]);
      }
      setActiveConnectionState(uri);
      return true;
    } catch (err) {
      console.error('NWC SDK check rejected:', err);
      // Still allow adding if they are offline, as fallback, but return true if validated
      const exists = connections.some(c => c.connectionString === uri);
      if (!exists) {
        setConnections(prev => [...prev, { connectionString: uri, alias: alias || 'Lightning Wallet' }]);
      }
      setActiveConnectionState(uri);
      return true;
    }
  };

  const removeConnection = (uri: string) => {
    setConnections(prev => prev.filter(c => c.connectionString !== uri));
    if (activeConnection === uri) {
      setActiveConnectionState(null);
    }
  };

  const getActiveConnection = () => {
    return connections.find(c => c.connectionString === activeConnection) || null;
  };

  /**
   * Triggers payment of an invoice via Alby SDK NWC provider
   */
  const sendPayment = async (connectionString: string, invoice: string): Promise<{ preimage: string }> => {
    const nwcProvider = new NostrWebLNProvider({
      nostrWalletConnectUrl: connectionString
    });
    try {
      await nwcProvider.enable();
      const res = await nwcProvider.sendPayment(invoice);
      nwcProvider.close();
      return { preimage: res.preimage || '' };
    } catch (err) {
      nwcProvider.close();
      throw err;
    }
  };

  return {
    connections,
    activeConnection,
    addConnection,
    removeConnection,
    setActiveConnection: setActiveConnectionState,
    getActiveConnection,
    sendPayment
  };
}
export default useNWC;
